import * as React from "react"
import { Minus, Plus } from "lucide-react"

import { cn } from "@/lib/utils"

export type PeriodOption = {
  value: string | number
  label: string
  price: number | string
}

export type PeriodInputProps = {
  periods: PeriodOption[]
  value?: string | number
  defaultValue?: string | number
  onValueChange?: (value: string | number, period: PeriodOption) => void
  minLabel?: string
  maxLabel?: string
  className?: string
  disabled?: boolean
}

function formatPrice(price: number | string) {
  if (typeof price === "string") {
    return price
  }

  return `${new Intl.NumberFormat("ru-RU").format(price)} руб.`
}

function findPeriodIndex(periods: PeriodOption[], currentValue: string | number | undefined) {
  if (periods.length === 0) {
    return 0
  }

  if (currentValue === undefined) {
    return 0
  }

  const index = periods.findIndex(period => String(period.value) === String(currentValue))
  const resolvedIndex = index === -1 ? 0 : index

  return Math.min(resolvedIndex, periods.length - 1)
}

export function PeriodInput({
  periods,
  value,
  defaultValue,
  onValueChange,
  minLabel,
  maxLabel,
  className,
  disabled = false,
}: PeriodInputProps) {
  const [uncontrolledValue, setUncontrolledValue] = React.useState(
    () => defaultValue ?? periods[0]?.value
  )

  const isControlled = value !== undefined
  const currentValue = isControlled ? value : uncontrolledValue
  const index = findPeriodIndex(periods, currentValue)

  if (periods.length === 0) {
    return null
  }

  const activePeriod = periods[index]
  const canDecrease = index > 0
  const canIncrease = index < periods.length - 1

  const setPeriodByIndex = (nextIndex: number) => {
    const period = periods[nextIndex]
    if (!period) {
      return
    }

    if (!isControlled) {
      setUncontrolledValue(period.value)
    }

    onValueChange?.(period.value, period)
  }

  return (
    <div className={cn("mb-4", className)}>
      <div className="mb-2.5 flex items-center justify-between rounded-lg bg-input px-4 py-1.5">
        <button
          type="button"
          disabled={disabled || !canDecrease}
          onClick={() => setPeriodByIndex(index - 1)}
          className="inline-flex cursor-pointer items-center justify-center text-foreground transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Уменьшить срок"
        >
          <Minus className="size-6" strokeWidth={2} />
        </button>

        <div className="relative min-w-0 flex-1 px-4 text-center">
          {periods.map((period, periodIndex) => (
            <div
              key={String(period.value)}
              className={cn(periodIndex === index ? "block" : "hidden")}
            >
              <div className="text-lg leading-6 text-foreground">{period.label}</div>
              <div className="text-base leading-5 text-muted-foreground">{formatPrice(period.price)}</div>
            </div>
          ))}
          <input
            type="text"
            readOnly
            tabIndex={-1}
            value={String(activePeriod.value)}
            className="pointer-events-none absolute opacity-0"
            aria-hidden
          />
        </div>

        <button
          type="button"
          disabled={disabled || !canIncrease}
          onClick={() => setPeriodByIndex(index + 1)}
          className="inline-flex cursor-pointer items-center justify-center text-foreground transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Увеличить срок"
        >
          <Plus className="size-6" strokeWidth={2} />
        </button>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-base leading-5 text-muted-foreground">
          {minLabel ?? periods[0].label}
        </span>
        <span className="text-base leading-5 text-muted-foreground">
          {maxLabel ?? periods[periods.length - 1].label}
        </span>
      </div>
    </div>
  )
}
